/**
 * @fileoverview
 * String case utility functions (titleCase, lowercase, uppercase, camelCase, snake_case, kebab-case).
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

const WORD_BREAK_REGEX = /[\s_-]+/;

export class CaseUtil {
  /**
   * Convert string to title case (e.g. "hello world" -> "Hello World").
   * Splits on spaces, underscores, and hyphens.
   * Normalizes status-style values: empty, "N.a.", "N.A." etc. return "NA" for consistent status display across modules.
   */
  static titleCase(value: string | null | undefined): string {
    if (value == null || value === '') return '';
    const trimmed = String(value).trim();
    if (trimmed === '') return '';
    const lower = trimmed.toLowerCase();
    // Normalize "N.A.", "N. A.", "n a" etc. to "NA" (strip dots/spaces and compare)
    if (lower.replaceAll(/[.\s]/g, '') === 'na') return 'NA';
    return trimmed
      .split(WORD_BREAK_REGEX)
      .map((word) => (word.length === 0 ? '' : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()))
      .join(' ');
  }

  /**
   * Convert string to lowercase.
   */
  static toLowerCase(value: string): string {
    return value == null ? '' : String(value).toLowerCase();
  }

  /**
   * Convert string to uppercase.
   */
  static toUpperCase(value: string): string {
    return value == null ? '' : String(value).toUpperCase();
  }

  /**
   * Convert to camelCase (e.g. "hello world" -> "helloWorld").
   */
  static camelCase(value: string): string {
    if (value == null || value === '') return '';
    const words = value.trim().split(WORD_BREAK_REGEX).filter(Boolean);
    return words
      .map((word, i) =>
        i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join('');
  }

  /**
   * Convert to snake_case (e.g. "Hello World" -> "hello_world").
   */
  static snakeCase(value: string): string {
    if (value == null || value === '') return '';
    return value
      .trim()
      .split(WORD_BREAK_REGEX)
      .filter(Boolean)
      .map((w) => w.toLowerCase())
      .join('_');
  }

  /**
   * Convert to kebab-case (e.g. "Hello World" -> "hello-world").
   */
  static kebabCase(value: string): string {
    if (value == null || value === '') return '';
    return value
      .trim()
      .split(WORD_BREAK_REGEX)
      .filter(Boolean)
      .map((w) => w.toLowerCase())
      .join('-');
  }
}
